import React, { useState, useEffect } from 'react';
import { Row, Col, Toast } from 'react-bootstrap';
import { isMobileOnly, isMobile, isTablet } from 'react-device-detect';
import { authenticatedFetch } from '../../../utils/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useDeviceInfo } from '../../DeviceInfo';
import { useSecurityData } from './hooks/useSecurityData';
import { useRosterOperations } from './hooks/useRosterOperations';
import SecurityHeader from './components/SecurityHeader';
import SecurityFilters from './components/SecurityFilters';
import LicenseModal from './components/LicenseModal';
import RosterSummaryTable from './components/RosterSummaryTable';
import RosterMobileTable from './components/RosterMobileTable';
import RosterDesktopTable from './components/RosterDesktopTable';
import '../managerReport.scss';
import './manageSecurity.scss';

const ManageSecurity = ({
    showSecurityCosting,
    preloadedData,
    setPreloadedData,
    dataLoaded,
    setDataLoaded,
    selectedSite: selectedSiteProp,
    setSelectedSite: setSelectedSiteProp,
    dateFilters: dateFiltersProp,
    setDateFilters: setDateFiltersProp,
    selectedFinancialWeek: selectedFinancialWeekProp,
    setSelectedFinancialWeek: setSelectedFinancialWeekProp,
    createShiftVals: createShiftValsProp,
    setCreateShiftVals: setCreateShiftValsProp
}) => {
    const { userId } = useAuth();
    const { deviceInfo } = useDeviceInfo();
    const params = { email: userId };

    const [showLicenseModal, setShowLicenseModal] = useState(false);

    const [internalSelectedSite, setInternalSelectedSite] = useState('Select Site');
    const [internalSelectedFinancialWeek, setInternalSelectedFinancialWeek] = useState({ description: 'Please Select' });
    const [internalCreateShiftVals, setInternalCreateShiftVals] = useState({ venueid: '', codb: '', bun: '' });
    const [internalDateFilters, setInternalDateFilters] = useState({ shiftStart: '', shiftFinish: '' });

    const selectedSite = selectedSiteProp !== undefined ? selectedSiteProp : internalSelectedSite;
    const setSelectedSite = setSelectedSiteProp || setInternalSelectedSite;
    const selectedFinancialWeek = selectedFinancialWeekProp !== undefined ? selectedFinancialWeekProp : internalSelectedFinancialWeek;
    const setSelectedFinancialWeek = setSelectedFinancialWeekProp || setInternalSelectedFinancialWeek;
    const createShiftVals = createShiftValsProp !== undefined ? createShiftValsProp : internalCreateShiftVals;
    const setCreateShiftVals = setCreateShiftValsProp || setInternalCreateShiftVals;
    const dateFilters = dateFiltersProp !== undefined ? dateFiltersProp : internalDateFilters;
    const setDateFilters = setDateFiltersProp || setInternalDateFilters;

    const preloaded = dataLoaded ? preloadedData : null;
    const {
        sites,
        roles,
        financialWeeks,
        securitySummary,
        loading: dataLoading,
        summaryLoading,
        dataFetched
    } = useSecurityData(params, deviceInfo, preloaded);

    useEffect(() => {
        if (dataFetched && !dataLoaded && sites.length > 0 && setPreloadedData && setDataLoaded) {
            setPreloadedData({ sites, roles, financialWeeks, securitySummary });
            setDataLoaded(true);
        }
    }, [dataFetched, dataLoaded, sites, roles, financialWeeks, securitySummary, setPreloadedData, setDataLoaded]);

    const {
        rosterData,
        setRosterData,
        rosterSummary,
        pendingShifts,
        setPendingShifts,
        completedPendingIds,
        markPendingAsCompleted,
        clearCompletedPendingShifts,
        loading: rosterLoading,
        futureDateRestrict,
        noRosterDataMsg,
        toast,
        hideToast,
        showToast,
        fetchRoster,
        fetchRosterSummary,
        duplicateShifts,
        duplicateWeek,
        removeShifts
    } = useRosterOperations(params, deviceInfo);

    useEffect(() => {
        const currentDate = new Date();
        const threeMonthsFromNow = new Date();
        threeMonthsFromNow.setMonth(currentDate.getMonth() + 3);

        setDateFilters({
            shiftStart: currentDate.toISOString().split('T')[0],
            shiftFinish: threeMonthsFromNow.toISOString().split('T')[0]
        });
    }, []);

    useEffect(() => {
        if (sites.length > 0 && selectedSite === 'Select Site') {
            const firstSite = sites[0];
            handleSiteSelect(firstSite.site_name, firstSite.venueID, firstSite.codb, firstSite.bun);
        }
    }, [sites, selectedSite]);

    useEffect(() => {
        if (selectedSite !== 'Select Site' && dateFilters.shiftStart && dateFilters.shiftFinish) {
            const timer = setTimeout(() => {
                fetchRoster(dateFilters.shiftStart, dateFilters.shiftFinish, selectedSite);
                fetchRosterSummary(dateFilters.shiftStart, dateFilters.shiftFinish, selectedSite);
            }, 300); 

            return () => clearTimeout(timer);
        }
    }, [selectedSite, dateFilters.shiftStart, dateFilters.shiftFinish]);

    const handleSiteSelect = (siteName, venueID, codb, bun) => {
        setSelectedSite(siteName);
        setCreateShiftVals({ venueid: venueID, codb, bun });
    };

    const handleDateChange = (key, value) => {
        setDateFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleFinancialWeekSelect = (financeWeek) => {
        setSelectedFinancialWeek(financeWeek);

        if (!financeWeek) {
            setDateFilters({ shiftStart: '', shiftFinish: '' });
            return;
        }

        const formatLocalDate = (dateString) => {
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        const formattedStartDate = formatLocalDate(financeWeek.weekCommencing);
        const formattedEndDate = formatLocalDate(financeWeek.weekEnding);

        setDateFilters({
            shiftStart: formattedStartDate,
            shiftFinish: formattedEndDate
        });
    };

    const handleRefresh = () => {
        clearCompletedPendingShifts();

        if (selectedSite !== 'Select Site' && dateFilters.shiftStart && dateFilters.shiftFinish) {
            fetchRoster(dateFilters.shiftStart, dateFilters.shiftFinish, selectedSite);
            fetchRosterSummary(dateFilters.shiftStart, dateFilters.shiftFinish, selectedSite);
        }
    };

    const handleClearFilters = () => {
        const currentDate = new Date();
        const threeMonthsFromNow = new Date();
        threeMonthsFromNow.setMonth(currentDate.getMonth() + 3);

        setDateFilters({
            shiftStart: currentDate.toISOString().split('T')[0],
            shiftFinish: threeMonthsFromNow.toISOString().split('T')[0]
        });

        setSelectedFinancialWeek({ description: 'Please Select' });

        if (sites.length > 0) {
            const firstSite = sites[0];
            setSelectedSite(firstSite.site_name);
            setCreateShiftVals({ venueid: firstSite.venueID, codb: firstSite.codb, bun: firstSite.bun });
        }
    };

    const handleShiftCreate = async (shiftData, pendingIds) => {
        try {
            const response = await authenticatedFetch('/ManagerReport/stonegate__createShift', {
                method: 'POST',
                body: JSON.stringify(shiftData),
            });

            if (!response.ok) {
                throw new Error('Failed to create shift');
            }

            markPendingAsCompleted(pendingIds);
            showToast('Success', `Shift created successfully! Click Refresh to update.`);
        } catch (error) {
            console.error('Error creating shift:', error);

            const idsArray = Array.isArray(pendingIds) ? pendingIds : [pendingIds];
            setPendingShifts(prev => prev.filter(p => !idsArray.includes(p.pendingId)));
            setRosterData(prev => prev.filter(p => !idsArray.includes(p.pendingId)));

            showToast('Error', `Failed to create shift: ${error.message}`);
        }
    };

    const loading = dataLoading || rosterLoading;

    return (
        <div className="manage-security-container">
            <Toast
                show={toast.visible}
                onClose={hideToast}
                delay={3000}
                autohide
                className="security-toast"
            >
                <Toast.Header>
                    <strong className="me-auto">{toast.title}</strong>
                </Toast.Header>
                <Toast.Body>{toast.message}</Toast.Body>
            </Toast>

            <LicenseModal
                show={showLicenseModal}
                onHide={() => setShowLicenseModal(false)}
                selectedSite={selectedSite}
                rosterData={rosterData}
            />

            <Row>
                <Col md="12">
                    {!(isMobileOnly || isTablet) && (
                        <div className="security-filters-container">
                            <div className="security-filters-left">
                                <SecurityFilters
                                    selectedSite={selectedSite}
                                    sites={sites}
                                    onSiteSelect={handleSiteSelect}
                                    dateFrom={dateFilters.shiftStart}
                                    dateTo={dateFilters.shiftFinish}
                                    onDateChange={handleDateChange}
                                    selectedFinancialWeek={selectedFinancialWeek}
                                    financialWeeks={financialWeeks}
                                    onFinancialWeekSelect={handleFinancialWeekSelect}
                                />
                            </div>
                            <SecurityHeader
                                onOpenLicense={() => setShowLicenseModal(true)}
                                onRefresh={handleRefresh}
                                onClearFilters={handleClearFilters}
                                loading={loading}
                                hasCompletedPendingShifts={completedPendingIds.size > 0}
                            />
                        </div>
                    )}

                    <div>
                        <div className="d-flex flex-column">
                            {loading ? (
                                <div className="small-spinner">
                                    <span></span><span></span><span></span><span></span>
                                </div>
                            ) : (
                                <div>
                                            <div className="report-table-container roster-main-table mt-3 pb-0">
                                                {(isMobileOnly || isTablet) ? (
                                                    <RosterMobileTable
                                                        mobileData={rosterData}
                                                        roles={roles}
                                                        financialWeeks={financialWeeks}
                                                        createShiftVals={createShiftVals}
                                                        futureDateRestrict={futureDateRestrict}
                                                        selectedSite={selectedSite}
                                                        onDuplicate={async (date, rows) => {
                                                            await duplicateShifts(date, rows, () => {
                                                                fetchRoster(dateFilters.shiftStart, dateFilters.shiftFinish, selectedSite);
                                                            });
                                                        }}
                                                        onDuplicateWeek={async (weekDate, destDate) => {
                                                            await duplicateWeek(weekDate, destDate, () => {
                                                                fetchRoster(dateFilters.shiftStart, dateFilters.shiftFinish, selectedSite);
                                                            });
                                                        }}
                                                        onRemove={async (rows) => {
                                                            await removeShifts(rows, () => {
                                                                fetchRoster(dateFilters.shiftStart, dateFilters.shiftFinish, selectedSite);
                                                            });
                                                        }}
                                                        onShiftCreate={handleShiftCreate}
                                                        pendingShifts={pendingShifts}
                                                        setPendingShifts={setPendingShifts}
                                                        setRosterData={setRosterData}
                                                        onRefresh={() => {
                                                            fetchRoster(dateFilters.shiftStart, dateFilters.shiftFinish, selectedSite);
                                                            fetchRosterSummary(dateFilters.shiftStart, dateFilters.shiftFinish, selectedSite);
                                                        }}
                                                        showToast={showToast}
                                                        toast={toast}
                                                        onToastClose={hideToast}
                                                    />
                                                ) : (
                                                    <RosterDesktopTable
                                                        rosterData={rosterData}
                                                        roles={roles}
                                                        financialWeeks={financialWeeks}
                                                        dateFilters={dateFilters}
                                                        selectedFinancialWeek={selectedFinancialWeek}
                                                        selectedSite={selectedSite}
                                                        createShiftVals={createShiftVals}
                                                        futureDateRestrictProp={futureDateRestrict}
                                                        pendingShifts={pendingShifts}
                                                        setPendingShifts={setPendingShifts}
                                                        completedPendingIds={completedPendingIds}
                                                        setRosterData={setRosterData}
                                                        onDuplicate={async (date, rows) => {
                                                            await duplicateShifts(date, rows, () => {
                                                                fetchRoster(dateFilters.shiftStart, dateFilters.shiftFinish, selectedSite);
                                                            });
                                                        }}
                                                        onDuplicateWeek={async (weekDate, destDate) => {
                                                            await duplicateWeek(weekDate, destDate, () => {
                                                                fetchRoster(dateFilters.shiftStart, dateFilters.shiftFinish, selectedSite);
                                                            });
                                                        }}
                                                        onRemove={async (rows) => {
                                                            await removeShifts(rows, () => {
                                                                fetchRoster(dateFilters.shiftStart, dateFilters.shiftFinish, selectedSite);
                                                            });
                                                        }}
                                                        onRefresh={() => {
                                                            fetchRoster(dateFilters.shiftStart, dateFilters.shiftFinish, selectedSite);
                                                            fetchRosterSummary(dateFilters.shiftStart, dateFilters.shiftFinish, selectedSite);
                                                        }}
                                                        onShiftCreate={handleShiftCreate}
                                                        showToast={showToast}
                                                        showSecurityCosting={showSecurityCosting}
                                                        noRosterDataMsg={noRosterDataMsg}
                                                    />
                                                )}
                                            </div>
                                            <div className="report-table-container roster-table mt-3 pb-0">
                                                {!isMobile && !isTablet && (
                                                    <RosterSummaryTable rosterSummary={rosterSummary} />
                                                )}
                                            </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default ManageSecurity;
